import { useState, useEffect, useCallback, useMemo } from 'react';
import { api, UserFavoriteWithArche } from '@/lib/api';

interface FavoritePlacement {
  archId: string;
  position: number;
}

/**
 * Full favorites management hook used by the Favorites page.
 * Provides the list, toggle, and placement-based reorder functionality.
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState<UserFavoriteWithArche[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const favoriteIds = useMemo(() => new Set(favorites.map((f) => f.archId)), [favorites]);

  const refresh = useCallback(async () => {
    try {
      const result = await api.getFavorites();
      setFavorites(result.favorites);
    } catch {
      // Silently fail — user may not be authenticated
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFavorite = useCallback((archId: string) => favoriteIds.has(archId), [favoriteIds]);

  const toggleFavorite = useCallback(
    async (archId: string) => {
      if (favoriteIds.has(archId)) {
        // Optimistic remove
        setFavorites((prev) => prev.filter((f) => f.archId !== archId));
        try {
          await api.removeFavorite(archId);
        } catch {
          await refresh();
        }
      } else {
        try {
          await api.addFavorite(archId);
          await refresh();
        } catch {
          // Ignore
        }
      }
    },
    [favoriteIds, refresh],
  );

  const reorder = useCallback(
    async (placements: FavoritePlacement[]) => {
      // Optimistic update — apply new positions locally
      setFavorites((prev) => {
        const placementMap = new Map(placements.map((p) => [p.archId, p.position]));
        return prev
          .map((f) => {
            const newPos = placementMap.get(f.archId);
            return newPos !== undefined ? { ...f, position: newPos } : f;
          })
          .sort((a, b) => a.position - b.position);
      });

      try {
        await api.reorderFavorites(placements);
      } catch {
        await refresh();
      }
    },
    [refresh],
  );

  return { favorites, isLoading, isFavorite, toggleFavorite, reorder, refresh };
}
